import { useFormContext } from 'react-hook-form'

import {
  type ShipmentOrderCardFormValues,
  type ShipmentOrderCardNamePrefix
} from './shipment-order-card-form-values'

/**
 * カード契約のキー、または任意の文字列（`items.0.itemName` のような添字付きパス）を許容する。
 * `keyof ShipmentOrderCardFormValues` を含めるのはエディタ補完を効かせるためで、
 * 実行時の検証は行わない（RHF の `name` は文字列パスであり型では検証できないため）。
 */
type ShipmentOrderCardFieldKey =
  | keyof ShipmentOrderCardFormValues
  | (string & Record<never, never>)

/**
 * ShipmentOrderFormCard 用の RHF フィールドアクセサ。
 * `namePrefix` 込みでフィールド名を組み立てることで、ルート直下（`''`）と
 * 矢羽根編集フォームの `shipments.${number}.` 配下の両方で同じカード実装を使い回せるようにする。
 * `useFormContext()` は既存の ShipmentOrderWaypointsSection 等と同じく型引数なしで使う
 * （呼び出し側フォームの値型が4種類に分かれ、単一の型引数では表現できないため）。
 */
export const useShipmentOrderCardField = (
  namePrefix: ShipmentOrderCardNamePrefix = ''
) => {
  const form = useFormContext()

  const name = (key: ShipmentOrderCardFieldKey): string => `${namePrefix}${key}`

  const watchField = (key: ShipmentOrderCardFieldKey): unknown =>
    form.watch(name(key))

  const setField = (
    key: ShipmentOrderCardFieldKey,
    value: unknown,
    options?: {
      shouldDirty?: boolean
      shouldValidate?: boolean
      shouldTouch?: boolean
    }
  ): void => {
    form.setValue(name(key), value, options)
  }

  return { name, watchField, setField, form }
}
